import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  ExternalLink,
  Film,
  Image as ImageIcon,
  Instagram,
  Loader2,
  Mic,
  PenSquare,
  RefreshCw,
  Music,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  Video,
  Waves,
  Youtube,
} from 'lucide-react';
import { isImageMediaUrl, isVideoMediaUrl, mediaUrlForPlayback, playableFinalVideoUrl } from '@/lib/mediaUrls';
import { exportPlatformLabel, fetchCreationOutputs, hasCreationOutputs, sfxStepLabel } from '@/lib/creationPlan';
import { narrationSceneImageUrl } from '@/lib/sceneGridNarrationAdapter';

const RUNNING_REFRESH_MS = 10000;
const TERMINAL_JOB_STATES = ['completed', 'failed', 'cancelled'];

export const PUBLISH_PLATFORMS = [
  { id: 'youtube', label: 'YouTube', Icon: Youtube, accent: 'text-[#F87171]' },
  { id: 'instagram', label: 'Instagram', Icon: Instagram, accent: 'text-[#F472B6]' },
];

/** Loads `/creation/plans/{id}/outputs` for an executed plan; refreshes while the job runs and when it finishes. */
export function useCreationOutputs(planId, execution, jobStatus) {
  const [outputs, setOutputs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const enabled = Boolean(planId && hasCreationOutputs(execution));
  const requestRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    const request = ++requestRef.current;
    setLoading(true);
    try {
      const data = await fetchCreationOutputs(planId);
      if (request !== requestRef.current) return data;
      setOutputs(data);
      setError('');
      return data;
    } catch (err) {
      if (request !== requestRef.current) return;
      setError(err?.response?.data?.detail || err?.message || 'Could not load outputs');
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [enabled, planId]);

  useEffect(() => {
    if (!enabled) {
      requestRef.current += 1;
      setOutputs(null);
      setError('');
      setLoading(false);
      return;
    }
    refresh();
  }, [enabled, refresh, execution?.job_id, jobStatus]);

  const running = enabled && Boolean(jobStatus) && !TERMINAL_JOB_STATES.includes(jobStatus);
  const autoExportBusy = ['running', 'retrying'].includes(
    String(outputs?.auto_export?.status || '').toLowerCase(),
  );
  const awaitingCompiledVideo = enabled
    && !playableFinalVideoUrl(outputs?.final_video_url)
    && !sceneVisualStats(outputs).incomplete
    && (
      autoExportBusy
      || (jobStatus === 'completed' && (outputs?.scenes || []).some((s) => narrationSceneImageUrl(s) || s.video_url))
    );
  useEffect(() => {
    if (!running && !awaitingCompiledVideo) return undefined;
    const timer = setInterval(refresh, autoExportBusy ? 4000 : RUNNING_REFRESH_MS);
    return () => clearInterval(timer);
  }, [running, awaitingCompiledVideo, autoExportBusy, refresh]);

  return { outputs, loading, error, refresh };
}

function isAbsoluteHttp(url) {
  return /^https?:\/\//i.test(String(url || ''));
}

/** How many Output scenes have a still or video. Partial sets are not a finished run. */
export function sceneVisualStats(outputs) {
  const scenes = Array.isArray(outputs?.scenes) ? outputs.scenes : [];
  const withVisual = scenes.filter((s) => narrationSceneImageUrl(s) || s.video_url).length;
  return {
    total: scenes.length,
    withVisual,
    incomplete: scenes.length > 0 && withVisual < scenes.length,
  };
}

export function incompleteImagesMessage(stats) {
  if (!stats?.incomplete) return '';
  return (
    `${stats.withVisual} of ${stats.total} scene images ready — generate the missing images ` +
    'before the final video is complete.'
  );
}

export function displayJobProgress(job, outputs) {
  const raw = Math.max(0, Math.min(100, Number(job?.progress) || 0));
  const stats = sceneVisualStats(outputs);
  const status = String(job?.status || '');
  if (!stats.incomplete || status === 'running' || status === 'pending') return raw;
  const fromScenes = stats.total ? Math.round((stats.withVisual / stats.total) * 90) : 0;
  return Math.min(raw, Math.max(0, fromScenes));
}

/** Compiled MP4 only — never the pipeline's first still that older jobs stored on result_url. */
export function resolvedFinalVideoUrl(outputs, job) {
  const fromOutputs = playableFinalVideoUrl(outputs?.final_video_url);
  if (fromOutputs) return fromOutputs;
  if (job?.status !== 'completed') return '';
  const fromJob = String(job?.result_url || '').trim();
  return isVideoMediaUrl(fromJob) ? fromJob : '';
}

/** Saves the MP4 locally; falls back to opening it in a new tab when the host blocks cross-origin reads. */
export async function downloadVideoFile(url, filename = 'twinnee-video.mp4') {
  if (isImageMediaUrl(url)) return;
  const src = mediaUrlForPlayback(url) || url;
  if (!src) return;
  const name = filename.endsWith('.mp4') ? filename : `${filename}.mp4`;
  try {
    const token = !isAbsoluteHttp(src) && typeof localStorage !== 'undefined' ? localStorage.getItem('storycraft_token') : null;
    const res = await fetch(src, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const type = String(blob.type || res.headers.get('content-type') || '').toLowerCase();
    if (type.startsWith('image/')) return;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(src, '_blank', 'noopener,noreferrer');
  }
}

export function videoFilename(title) {
  const slug = String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug || 'twinnee-video'}.mp4`;
}

function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function latestPublish(outputs, platform) {
  const row = (outputs?.exports || []).find((r) => r.platform === platform && r.publish_url);
  if (row) return row.publish_url;
  return outputs?.auto_publish?.[`${platform}_url`] || '';
}

function PublishCard({ platform, outputs, publish, canPublish, onPublish }) {
  const { id, label, Icon, accent } = platform;
  const social = outputs?.social?.[id] || {};
  const state = publish?.[id] || {};
  const postedUrl = state.url || latestPublish(outputs, id);
  const connected = Boolean(social.connected);

  let authLine = 'Checking…';
  if (outputs) {
    if (!social.configured) authLine = 'Not configured on this server';
    else if (connected) authLine = social.account ? `Connected as ${social.account}` : 'Connected';
    else authLine = 'Not connected';
  }

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2" data-testid={`create-plan-publish-${id}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${accent}`} />
        <span className="text-xs font-semibold text-white">{label}</span>
        <span
          className={`ml-auto inline-flex items-center gap-1 text-[10px] ${connected ? 'text-[#2DD4BF]' : 'text-[#9CA3AF]'}`}
        >
          {connected ? <CheckCircle2 className="w-3 h-3" /> : null}
          <span className="truncate max-w-[140px]">{authLine}</span>
        </span>
      </div>

      {state.status === 'publishing' ? (
        <p className="text-[11px] text-[#7DD3FC] flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" /> Posting to {label}… keep this tab open.
        </p>
      ) : state.status === 'failed' ? (
        <p className="text-[11px] text-[#FCA5A5] flex items-start gap-1.5 break-words">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" /> {state.error || 'Posting failed'}
        </p>
      ) : postedUrl ? (
        <a
          href={postedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-[#2DD4BF] hover:underline flex items-center gap-1 truncate"
        >
          <CheckCircle2 className="w-3 h-3 shrink-0" /> Posted — open on {label} <ExternalLink className="w-3 h-3 shrink-0" />
        </a>
      ) : (
        <p className="text-[11px] text-[#6B7280]">Not posted yet.</p>
      )}

      <button
        type="button"
        disabled={!canPublish || state.status === 'publishing' || (outputs && !social.configured)}
        onClick={() => onPublish(id)}
        className="w-full py-1.5 rounded-lg border border-white/15 text-[11px] font-semibold text-[#E5E7EB] hover:bg-white/5 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {connected ? (postedUrl ? `Post again to ${label}` : `Publish to ${label}`) : `Connect ${label} & publish`}
      </button>
    </div>
  );
}

function SceneTile({ scene, waiting = false }) {
  const [failed, setFailed] = useState(false);
  // Same still field resolve as Scene View / Editor / Workflow.
  const stillUrl = narrationSceneImageUrl(scene);
  const image = mediaUrlForPlayback(stillUrl);
  const identity = String(scene.identity_status || '').toLowerCase();
  const identityOk = identity === 'pass';
  const identityBad = identity === 'fail';
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 overflow-hidden">
      <div className="aspect-video bg-white/[0.03] flex items-center justify-center relative">
        {image && !failed ? (
          <img src={image} alt="" loading="lazy" onError={() => setFailed(true)} className="w-full h-full object-cover" />
        ) : waiting ? (
          <Loader2 className="w-3.5 h-3.5 text-[#4B5563] animate-spin" />
        ) : (
          <span className="text-[10px] text-[#6B7280]">No image</span>
        )}
        {identityOk ? (
          <span
            className="absolute top-1 right-1 rounded bg-black/60 p-0.5"
            title="Character identity validated"
            data-testid={`scene-identity-pass-${scene.number}`}
          >
            <ShieldCheck className="w-3 h-3 text-[#2DD4BF]" />
          </span>
        ) : null}
        {identityBad ? (
          <span
            className="absolute top-1 right-1 rounded bg-black/60 p-0.5"
            title={`Identity issues: ${scene.identity_mismatches || 0}`}
            data-testid={`scene-identity-fail-${scene.number}`}
          >
            <ShieldAlert className="w-3 h-3 text-[#FCA5A5]" />
          </span>
        ) : null}
      </div>
      <div className="px-2 py-1.5 space-y-1">
        <div className="flex items-center gap-1.5 text-[10px]">
          <span className="font-mono text-[#9CA3AF]">#{scene.number}</span>
          <ImageIcon className={`w-3 h-3 ${stillUrl ? 'text-[#2DD4BF]' : 'text-[#4B5563]'}`} />
          <Video className={`w-3 h-3 ${scene.video_url ? 'text-[#2DD4BF]' : 'text-[#4B5563]'}`} />
          <Mic className={`w-3 h-3 ${scene.has_audio ? 'text-[#2DD4BF]' : 'text-[#4B5563]'}`} />
          {scene.has_sfx ? <Waves className="w-3 h-3 text-[#2DD4BF]" aria-label="Sound effects" /> : null}
        </div>
        {scene.text ? <p className="text-[10px] text-[#9CA3AF] line-clamp-2">{scene.text}</p> : null}
      </div>
    </div>
  );
}

/**
 * Right-panel "Output" view of the create agent: the finished video (or what's been generated so far),
 * download + YouTube/Instagram publishing with auth and posting status, and the export history.
 */
export function CreatePlanOutputPanel({
  outputs,
  loading,
  error,
  job,
  publish,
  onPublish,
  onRefresh,
  onOpenEditor,
  onRerender,
  rerendering = false,
  onConvertImagesToVideos,
  convertingImages = false,
  onRetryExport,
  retryingExport = false,
}) {
  const jobRunning = job?.status && !TERMINAL_JOB_STATES.includes(job.status);
  const finalUrl = jobRunning ? '' : resolvedFinalVideoUrl(outputs, job);
  const exportRow = (outputs?.exports || []).find((r) => !r.platform && r.url === finalUrl);
  const playable = finalUrl ? mediaUrlForPlayback(finalUrl, { localUrl: exportRow?.local_url || undefined }) : '';
  const scenes = outputs?.scenes || [];
  const visualStats = sceneVisualStats(outputs);
  const exports = outputs?.exports || [];
  const vertical = String(outputs?.aspect_ratio || '').startsWith('9:');
  const editorUrl = outputs?.editor_url || '';
  const canRerender = Boolean(outputs?.can_rerender && onRerender) && !jobRunning;
  const pendingAiVideos = Number(outputs?.pending_ai_videos) || 0;
  const canConvertImages = Boolean(outputs?.can_convert_images_to_videos && onConvertImagesToVideos)
    && pendingAiVideos > 0
    && !jobRunning;
  const sound = outputs?.sound_design || null;
  const soundReady = sound?.status === 'ready';
  const sfxLabel = sfxStepLabel(sound);
  const withSfx = scenes.filter((s) => s.has_sfx).length;
  const withVideo = scenes.filter((s) => s.video_url).length;
  const withImage = scenes.filter((s) => narrationSceneImageUrl(s)).length;
  const autoExport = outputs?.auto_export || null;
  const autoExportStatus = String(autoExport?.status || '').toLowerCase();
  const autoExportBusy = ['running', 'retrying'].includes(autoExportStatus);
  const autoExportFailed = ['failed', 'stuck', 'blocked'].includes(autoExportStatus)
    && Boolean(autoExport?.can_manual_retry || (Number(autoExport?.attempts) || 0) >= (Number(autoExport?.max_attempts) || 3));
  const compiling = !finalUrl
    && !visualStats.incomplete
    && (autoExportBusy || (withImage > 0 && !autoExportFailed && job?.status === 'completed'));

  if (!outputs && loading) {
    return (
      <div className="self-center flex items-center gap-2 text-sm text-[#9CA3AF]">
        <Loader2 className="w-4 h-4 animate-spin text-[#2DD4BF]" /> Loading outputs…
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl space-y-3" data-testid="create-plan-output-panel">
      {error ? (
        <p className="text-[11px] text-[#FCA5A5] flex items-center gap-1.5">
          <AlertCircle className="w-3 h-3" /> {error}
        </p>
      ) : null}

      <div className="rounded-2xl border border-white/10 bg-[#11151e]/95 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Film className="w-4 h-4 text-[#2DD4BF]" />
          <p className="text-sm font-semibold text-white truncate">{outputs?.title || 'Your video'}</p>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            aria-label="Refresh outputs"
            className="ml-auto p-1.5 rounded-lg text-[#9CA3AF] hover:text-white hover:bg-white/10 cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {playable ? (
          <video
            key={playable}
            src={playable}
            controls
            playsInline
            preload="metadata"
            data-testid="create-plan-output-video"
            className={`rounded-xl bg-black ${vertical ? 'block mx-auto h-[60vh] max-w-full aspect-[9/16]' : 'w-full aspect-video'}`}
          />
        ) : (
          <div className="aspect-video rounded-xl border border-dashed border-white/15 bg-black/30 flex flex-col items-center justify-center gap-2 text-center px-6">
            {jobRunning || compiling ? <Loader2 className="w-5 h-5 animate-spin text-[#2DD4BF]" /> : <Film className="w-5 h-5 text-[#4B5563]" />}
            <p className="text-xs text-[#9CA3AF]" data-testid="create-plan-output-empty">
              {jobRunning
                ? `${job?.message || 'Generating'} · ${displayJobProgress(job, outputs)}%`
                : visualStats.incomplete
                  ? incompleteImagesMessage(visualStats)
                  : compiling
                    ? `Compiling final video${autoExport?.attempts ? ` · attempt ${autoExport.attempts}/${autoExport.max_attempts || 3}` : '…'} — Export unlocks when ready (no Open project needed).`
                    : autoExportFailed
                      ? `Final video compile failed after ${autoExport?.attempts || 3} attempts${autoExport?.error ? `: ${autoExport.error}` : ''}.`
                      : withImage
                        ? 'Scenes are ready — compiling the final MP4 automatically…'
                        : 'The final video appears here once a compiled MP4 is available.'}
            </p>
            {sfxLabel ? (
              <p className="text-[11px] text-[#5EEAD4]" data-testid="create-plan-output-sfx">
                {sfxLabel}
              </p>
            ) : null}
          </div>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!finalUrl}
            onClick={() => downloadVideoFile(finalUrl, videoFilename(outputs?.title))}
            title={finalUrl ? 'Download MP4' : compiling ? 'Compiling final video…' : 'Export unlocks when the compiled MP4 is ready'}
            className="flex-1 py-2 rounded-xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] text-xs font-bold cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="create-plan-output-download"
          >
            <Download className="w-3.5 h-3.5" /> Export final video (MP4)
          </button>
          {editorUrl && onOpenEditor ? (
            <button
              type="button"
              onClick={() => onOpenEditor(editorUrl)}
              className="px-4 py-2 rounded-xl border border-white/15 text-xs font-bold text-[#E5E7EB] hover:bg-white/5 cursor-pointer flex items-center justify-center gap-1.5 uppercase tracking-wide"
              data-testid="create-plan-output-open-editor"
            >
              <PenSquare className="w-3.5 h-3.5" /> Open in editor
            </button>
          ) : null}
        </div>
        {autoExportFailed && !visualStats.incomplete && onRetryExport ? (
          <button
            type="button"
            onClick={() => onRetryExport()}
            disabled={retryingExport || compiling}
            className="w-full py-2.5 rounded-xl border border-[#FBBF24]/40 bg-[#FBBF24]/10 text-[#FDE68A] text-xs font-bold cursor-pointer flex items-center justify-center gap-1.5 hover:bg-[#FBBF24]/15 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-plan-output-retry-export"
          >
            {retryingExport ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Retry final video compile
          </button>
        ) : null}
        {canConvertImages ? (
          <button
            type="button"
            onClick={() => onConvertImagesToVideos()}
            disabled={convertingImages || rerendering}
            className="w-full py-2.5 rounded-xl border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 text-[#5EEAD4] text-xs font-bold cursor-pointer flex items-center justify-center gap-1.5 hover:bg-[#2DD4BF]/15 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-plan-output-convert-videos"
          >
            {convertingImages ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />}
            Convert all images to videos
            <span className="font-normal text-[#9CA3AF]">· {pendingAiVideos} scene{pendingAiVideos === 1 ? '' : 's'} · multi-speaker focus</span>
          </button>
        ) : null}
        {sfxLabel && playable ? (
          <p className="text-[11px] text-[#5EEAD4] flex items-center justify-center gap-1.5" data-testid="create-plan-output-sfx">
            <Waves className="w-3 h-3" /> {sfxLabel}
          </p>
        ) : null}
        {sound && soundReady ? (
          <p className="text-[11px] text-[#9CA3AF] flex items-center justify-center gap-1.5" data-testid="create-plan-output-sound">
            <Music className="w-3 h-3 text-[#2DD4BF]" />
            {sound.music_ready ? `${sound.music_ready} music cue${sound.music_ready === 1 ? '' : 's'}` : 'No background music'}
            {' · '}
            {withSfx ? `sound effects in ${withSfx} scene${withSfx === 1 ? '' : 's'}` : 'no sound effects'}
          </p>
        ) : null}
        {canRerender ? (
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <button
              type="button"
              onClick={() => onRerender()}
              disabled={rerendering}
              className="text-[11px] text-[#9CA3AF] hover:text-white flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="create-plan-output-rerender"
            >
              {rerendering ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Video out of date? Re-render it from the current scenes
            </button>
            {sound ? (
              <button
                type="button"
                onClick={() => onRerender({ soundDesign: true })}
                disabled={rerendering}
                className="text-[11px] text-[#7DD3FC] hover:text-white flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="create-plan-output-sound-design"
              >
                <Music className="w-3 h-3" />
                {soundReady ? 'Redo music & sound effects' : 'Add music & sound effects'}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {PUBLISH_PLATFORMS.map((platform) => (
          <PublishCard
            key={platform.id}
            platform={platform}
            outputs={outputs}
            publish={publish}
            canPublish={Boolean(finalUrl)}
            onPublish={onPublish}
          />
        ))}
      </div>

      {scenes.length ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Scenes so far</p>
            <p className="text-[10px] font-mono text-[#9CA3AF]">
              {withImage}/{scenes.length} images · {withVideo}/{scenes.length} videos
            </p>
          </div>
          {visualStats.incomplete ? (
            <p className="text-[11px] text-[#FBBF24]" data-testid="create-plan-output-images-incomplete">
              {incompleteImagesMessage(visualStats)}
            </p>
          ) : null}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {scenes.map((scene) => (
              <SceneTile key={scene.number} scene={scene} waiting={jobRunning} />
            ))}
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-2">
        <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Export history</p>
        {exports.length ? (
          <table className="w-full text-[11px]" data-testid="create-plan-export-history">
            <thead>
              <tr className="text-left text-[#6B7280]">
                <th className="font-medium py-1 pr-2">Platform</th>
                <th className="font-medium py-1 pr-2">Date</th>
                <th className="font-medium py-1 pr-2">Format</th>
                <th className="font-medium py-1 text-right">Link</th>
              </tr>
            </thead>
            <tbody>
              {exports.map((row, idx) => {
                const link = row.platform ? row.publish_url : mediaUrlForPlayback(row.url, { localUrl: row.local_url || undefined });
                return (
                  <tr key={`${row.platform || 'file'}-${row.created_at}-${idx}`} className="border-t border-white/5 text-[#E5E7EB]">
                    <td className="py-1.5 pr-2">
                      <span className="inline-flex items-center gap-1">
                        {row.platform === 'youtube' ? (
                          <Youtube className="w-3 h-3 text-[#F87171]" />
                        ) : row.platform === 'instagram' ? (
                          <Instagram className="w-3 h-3 text-[#F472B6]" />
                        ) : (
                          <Film className="w-3 h-3 text-[#9CA3AF]" />
                        )}
                        {exportPlatformLabel(row.platform)}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2 text-[#9CA3AF]">{formatWhen(row.created_at)}</td>
                    <td className="py-1.5 pr-2 text-[#9CA3AF]">{row.aspect_ratio || '—'}</td>
                    <td className="py-1.5 text-right">
                      {link ? (
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[#2DD4BF] hover:underline"
                        >
                          {row.platform ? 'Open' : 'Video'} <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="text-[11px] text-[#6B7280]">No exports yet — rendered videos and YouTube/Instagram posts are listed here.</p>
        )}
      </div>
    </div>
  );
}

export default CreatePlanOutputPanel;
